import CustomProductQuantityBreakPricing from "@crescentParts/Components/CustomProductQuantityBreakPricing";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { InputStyle, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        view: state.pages.productList.view,
        isAuthenticated,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    addToListWrapperListView?: InjectableCss;
    quantityHeading?: TypographyPresentationProps;
    addToCartWrapperTableView?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            padding-left: 20px;
            padding-right: 20px;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
                font-size: 14px;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 2px;
            flex-grow: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column;
                            gap: 15px;
                            align-items: center;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    addToCartWrapperTableView: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 0;
        `,
    },
    addToCartButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                        css`
                            width: 80%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                width: 4.1rem;
                min-width: 4.1rem;
                > ${FormInputWrapper} {
                    > ${InputStyle} {
                        &:focus,
                        &:focus-within {
                            border: 2px solid ${getColor("common.border")} !important;
                            border-radius: 6px;
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,

                            css`
                                margin-right: 10px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 12px;
            min-height: 2rem;
            text-align: center;
        `,
    },
    addToListWrapperListView: {
        css: css`
            margin-top: 18px;
            min-height: 2rem;
        `,
    },
    quantityHeading: {
        variant: "headerSecondary",
        color: "text.accent",
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    view,
    isAuthenticated,
}) => {
    const addToListWRapperStyles = view !== "Grid" ? styles.addToListWrapperListView : styles.addToListWrapper;
    const addToCartWrapperStyles = view === "Table" ? styles.addToCartWrapperTableView : styles.addToCartWrapper;
    return (
        <StyledWrapper {...styles.wrapper}>
            {view === "Table" && isAuthenticated && (
                <Typography {...styles.quantityHeading}>{translate("QTY")}</Typography>
            )}

            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <CustomProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...addToCartWrapperStyles}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...addToListWRapperStyles}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
